import React, { useState } from 'react';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { CpsSearchQueryRequest, CpsSearchExportRequest } from '@cs-cms/types';
import { useCurrentUserContext } from '../contexts/CurrentUserContext';
import { useGlobalizationContext } from '../contexts/GlobalizationContext';
import { SearchContext } from '../contexts/SearchContext';
import { useTranslation } from '../hooks/globalization/useTranslation';
import { useSearch } from '../hooks/search/useSearch';
import { useTriggerExportToCsv } from '../hooks/search/useSearchExport';
import { useNotificationContext } from '../hooks/useNotificationContext';

interface ViewsProviderProps {
  children?: React.ReactNode;
}

export const SearchProvider: React.FC<ViewsProviderProps> = ({ children }) => {
  const { selectedIndex } = useCurrentUserContext();
  const [queryRequest, setQueryRequest] = useState<CpsSearchQueryRequest | undefined>(undefined);
  const { data, isLoading } = useSearch(queryRequest);
  const [compositeFilterDescriptor, setCompositeFilterDescriptor] = useState<CompositeFilterDescriptor | undefined>();
  const { mutate: mutateTriggerExportToCsv } = useTriggerExportToCsv();
  const { showToast } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  React.useEffect(() => {
    setQueryRequest(undefined);
    setCompositeFilterDescriptor(undefined);
  }, [selectedIndex?.id]);

  const handleTriggerExportToCsv = (dto: CpsSearchExportRequest): void => {
    mutateTriggerExportToCsv(
      { dto: { ...dto, indexId: selectedIndex?.id } },
      {
        onSuccess: () => {
          showToast({
            type: 'success',
            message: translate('custom.notifications.exportCsv')
          });
        }
      }
    );
  };

  return (
    <SearchContext.Provider
      value={{
        data,
        isLoading,
        queryRequest,
        setQueryRequest,
        compositeFilterDescriptor,
        setCompositeFilterDescriptor,
        handleTriggerExportToCsv
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
